import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button, Input, InputError, InputWrapperAuth } from '@/components/ui';
import { z } from 'zod';
import { ArrowRightCircle, CalendarDays, IdCard, Phone } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	clearStepData,
	getStepData,
	saveStepData,
} from '@/utils/localStorageUtils';
import { useEffect } from 'react';
import {
	maskCPF,
	maskDate,
	maskPhone,
	removeMask,
	normalizeName,
	transformBirthday,
	checkBenefitRedirection,
	clearPendingBenefitData,
	markUserFirstAccess,
	clearSchedulingPayDraftSilently,
	schedulingSelectedDateStillBookable,
	SCHEDULING_REGISTRATION_PENDING_KEY,
	forwardAuthRedirectNavigateOptions,
	getRegisterInitFromLocation,
	forwardRegisterFlowNavigateOptions,
} from '@/utils';
import { useResumeSchedulingPayAfterAuth } from '@/hooks/useResumeSchedulingPayAfterAuth';
import {
	consumeMatchReturnPending,
	MATCH_PAGE_PATH,
} from '@/utils/matchHandoff';
import { STORAGE_USER_KEYS } from './UserRegister';

import { useCreateUser, usePatchUserComplement } from '@/services';
import { setUserData } from '@/utils/cookies';
import TermsOfUsePage from '@/pages/terms-of-use';
import { useModal } from '@/hooks/useModal';
import PrivacyPolicyPage from '@/pages/public/privacy-policy';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/pages/public/landing/components/Header';
import { E2E_USER_AUTH } from '@/constants/e2eSchedulingTestIds';

const schema = z.object({
	first_name: z
		.string()
		.regex(
			/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
			'Nome inválido, apenas letras são permitidas',
		)
		.transform(normalizeName)
		.refine((val: string) => val.length >= 3, 'Mínimo 3 caracteres'),
	last_name: z
		.string()
		.regex(
			/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
			'Sobrenome inválido, apenas letras são permitidas',
		)
		.transform(normalizeName)
		.refine((val: string) => val.length >= 3, 'Mínimo 3 caracteres'),
	birthday: z
		.string()
		.regex(
			/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19\d{2}|20[01]\d|202[0-9])$/,
			'Data inválida (dd/mm/aaaa)',
		)
		.transform(transformBirthday),
	phone_number: z
		.string()
		.min(1, 'Telefone é obrigatório')
		.transform(removeMask)
		.refine(
			(val) => val.length === 11,
			'Número de telefone deve ter 11 dígitos',
		)
		.refine(
			(val) => /^\d+$/.test(val),
			'Número de telefone deve conter apenas números',
		),
	cpf: z
		.string()
		.transform(removeMask)
		.refine((val) => !val || val.length === 11, 'CPF deve ter 11 dígitos')
		.refine(
			(val) => !val || /^\d+$/.test(val),
			'CPF deve conter apenas números',
		),
	company_code: z.string().optional(),
	terms_accepted: z.literal(true, {
		errorMap: () => ({ message: 'Você deve aceitar os termos de uso' }),
	}),
});

const UserRegisterComplementation = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();

	type FormData = z.infer<typeof schema>;

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(schema),
	});

	const { mutateAsync } = useCreateUser();
	const { mutateAsync: googleFlowMutateAsync } = usePatchUserComplement();

	const { openModal } = useModal();
	const { resumeSchedulingPayAfterAuth } = useResumeSchedulingPayAfterAuth();

	const isGoogleFlow = searchParams.get('isG') === 'true';

	const resolveRegisterInitData = () =>
		getRegisterInitFromLocation(location.state) ??
		getStepData(STORAGE_USER_KEYS.USER_AUTH, STORAGE_USER_KEYS.STEP_INIT);

	useEffect(() => {
		const fromState = getRegisterInitFromLocation(location.state);
		if (fromState) {
			saveStepData(
				STORAGE_USER_KEYS.USER_AUTH,
				STORAGE_USER_KEYS.STEP_INIT,
				fromState,
			);
		}

		if (isGoogleFlow) return;

		const registerInit =
			fromState ??
			getStepData(
				STORAGE_USER_KEYS.USER_AUTH,
				STORAGE_USER_KEYS.STEP_INIT,
			);

		if (!registerInit) {
			navigate('/cadastro', { replace: true });
		}
	}, [isGoogleFlow, location.state, navigate]);

	useEffect(() => {
		const previousComplementStepSavedData = getStepData(
			STORAGE_USER_KEYS.USER_AUTH,
			STORAGE_USER_KEYS.STEP_COMPLEMENTATION,
		) as Partial<FormData> | null;

		if (!previousComplementStepSavedData) {
			return;
		}

		const fieldsToHydrate: Array<keyof FormData> = [
			'first_name',
			'last_name',
			'birthday',
			'phone_number',
			'cpf',
			'company_code',
			'terms_accepted',
		];

		fieldsToHydrate.forEach((field) => {
			const value = previousComplementStepSavedData[field];
			if (value !== undefined) {
				setValue(field, value as FormData[typeof field], {
					shouldValidate: false,
				});
			}
		});
	}, [setValue]);

	const watchedComplementData = watch();
	const canSubmitComplementForm =
		!!watchedComplementData.first_name?.trim() &&
		!!watchedComplementData.last_name?.trim() &&
		!!watchedComplementData.birthday?.trim() &&
		!!watchedComplementData.phone_number?.trim() &&
		!!watchedComplementData.cpf?.trim() &&
		watchedComplementData.terms_accepted === true;

	useEffect(() => {
		saveStepData(
			STORAGE_USER_KEYS.USER_AUTH,
			STORAGE_USER_KEYS.STEP_COMPLEMENTATION,
			watchedComplementData,
		);
	}, [watchedComplementData]);

	useEffect(() => {
		const clearRegisterDraftOnReload = () => {
			clearStepData(STORAGE_USER_KEYS.USER_AUTH);
		};

		window.addEventListener('beforeunload', clearRegisterDraftOnReload);

		return () => {
			window.removeEventListener(
				'beforeunload',
				clearRegisterDraftOnReload,
			);
		};
	}, []);

	const onSubmit = async (data: FormData) => {
		const previusStepSavedData = resolveRegisterInitData();

		if (!previusStepSavedData) {
			toast.error('Dados iniciais não encontrados. Recomece o cadastro.');
			navigate('/cadastro', {
				...forwardRegisterFlowNavigateOptions(location.state),
			});
			return;
		}

		const { terms_accepted, ...complementaryData } = data;

		const handleSuccess = (res: { success: boolean; user: any }) => {
			if (!res.success) return;

			setUserData(res.user);

			// Cadastro iniciado no /match: volta para enviar as respostas e ver o resultado.
			if (consumeMatchReturnPending()) {
				clearStepData(STORAGE_USER_KEYS.USER_AUTH);
				markUserFirstAccess();
				navigate(MATCH_PAGE_PATH, { replace: true });
				return;
			}

			const b2bFlow = checkBenefitRedirection();
			if (b2bFlow.shouldRedirect && b2bFlow.path) {
				clearStepData(STORAGE_USER_KEYS.USER_AUTH);
				clearPendingBenefitData();
				markUserFirstAccess();
				navigate(b2bFlow.path, { state: b2bFlow.state });
				return;
			}

			const isSchedulingContinuation =
				localStorage.getItem('isSchedulingFlow') === 'true' &&
				localStorage.getItem(SCHEDULING_REGISTRATION_PENDING_KEY) ===
					'true';
			if (isSchedulingContinuation) {
				const sessionDataString = localStorage.getItem('session_data');
				if (!sessionDataString) return;

				const completeSessionData = JSON.parse(
					sessionDataString,
				) as Record<string, unknown> & { user_id?: string };
				const { user_id: _ignoredUserId, ...body } =
					completeSessionData;
				const schedulingBody = body as unknown as Sessions.Create;

				if (
					!schedulingSelectedDateStillBookable(
						schedulingBody.selected_date,
					)
				) {
					clearSchedulingPayDraftSilently();
					clearStepData(STORAGE_USER_KEYS.USER_AUTH);
					markUserFirstAccess();
					navigate('/agendamento', { replace: true });
					return;
				}

				clearStepData(STORAGE_USER_KEYS.USER_AUTH);
				localStorage.removeItem(SCHEDULING_REGISTRATION_PENDING_KEY);
				markUserFirstAccess();
				void resumeSchedulingPayAfterAuth(navigate);
				return;
			} else {
				clearStepData(STORAGE_USER_KEYS.USER_AUTH);
				const clubFlowOpts = forwardAuthRedirectNavigateOptions(
					location.state,
				);

				if (clubFlowOpts.state) {
					markUserFirstAccess();
					navigate('/formulario', { replace: true, ...clubFlowOpts });
					return;
				}

				markUserFirstAccess();
				navigate('/primeiro-acesso', clubFlowOpts);
			}
		};

		try {
			if (isGoogleFlow) {
				// Payload para o fluxo Google (patch): NÃO envia o email
				const payloadForGoogleFlow = {
					id: previusStepSavedData.id,
					password: previusStepSavedData.password, // Email não é enviado aqui
					...complementaryData,
				};
				const res = await googleFlowMutateAsync(payloadForGoogleFlow);
				handleSuccess(res);
			} else {
				// Payload para o fluxo padrão (create): ENVIA o email
				const payloadForStandardFlow = {
					email: previusStepSavedData.email,
					password: previusStepSavedData.password,
					...complementaryData,
				};
				const res = await mutateAsync(payloadForStandardFlow);
				handleSuccess(res);
			}
		} catch (error) {
			toast.error('Erro ao finalizar cadastro. Verifique os dados.');
		}
	};

	const iconCommonClass = 'w-4 h-4 text-stone-400';

	return (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Complete seu cadastro | Plumaa</title>
			</Helmet>

			<Header />

			<main className="flex flex-1 items-center justify-center p-2 py-8">
				<div className="flex w-full max-w-[460px] flex-col gap-5 rounded-[2rem] bg-white p-5 shadow-sm md:p-8">
					<div className="space-y-0.5 text-center">
						<h1 className="text-xl font-black tracking-tight text-stone-900 md:text-2xl">
							Quase lá...
						</h1>
						<p className="text-[10px] font-medium text-stone-400 md:text-xs">
							Só precisamos de algumas informações
						</p>
					</div>

					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-4"
					>
						<div className="space-y-3">
							<div className="grid grid-cols-2 gap-3">
								<div className="space-y-1">
									<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
										NOME
									</label>
									<InputWrapperAuth
										className="max-w-none"
										icon={
											<IdCard
												className={iconCommonClass}
											/>
										}
										error={errors.first_name?.message}
										value={watch('first_name')}
										displayError
									>
										<Input
											{...register('first_name')}
											data-testid={
												E2E_USER_AUTH.COMPLEMENT_FIRST_NAME
											}
											value={watch('first_name') || ''}
											onChange={(e) =>
												setValue(
													'first_name',
													e.target.value.replace(
														/\s+/g,
														' ',
													),
													{ shouldValidate: false },
												)
											}
											placeholder="Nome"
											className="h-9 border-stone-200 text-sm"
										/>
									</InputWrapperAuth>
								</div>
								<div className="space-y-1">
									<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
										SOBRENOME
									</label>
									<InputWrapperAuth
										className="max-w-none"
										icon={
											<IdCard
												className={iconCommonClass}
											/>
										}
										error={errors.last_name?.message}
										value={watch('last_name')}
										displayError
									>
										<Input
											{...register('last_name')}
											data-testid={
												E2E_USER_AUTH.COMPLEMENT_LAST_NAME
											}
											value={watch('last_name') || ''}
											onChange={(e) =>
												setValue(
													'last_name',
													e.target.value.replace(
														/\s+/g,
														' ',
													),
													{ shouldValidate: false },
												)
											}
											placeholder="Sobrenome"
											className="h-9 border-stone-200 text-sm"
										/>
									</InputWrapperAuth>
								</div>
							</div>

							<div className="space-y-1">
								<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
									DATA DE NASCIMENTO
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={
										<CalendarDays
											className={iconCommonClass}
										/>
									}
									error={errors.birthday?.message}
									value={watch('birthday')}
									displayError
								>
									<Input
										value={maskDate(
											watch('birthday') || '',
										)}
										{...register('birthday')}
										data-testid={
											E2E_USER_AUTH.COMPLEMENT_BIRTHDAY
										}
										onChange={(e) =>
											setValue(
												'birthday',
												maskDate(e.target.value),
												{
													shouldValidate: true,
												},
											)
										}
										placeholder="dd/mm/aaaa"
										maxLength={10}
										className="h-9 border-stone-200 text-sm"
									/>
								</InputWrapperAuth>
							</div>

							<div className="space-y-1">
								<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
									CPF
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={
										<IdCard className={iconCommonClass} />
									}
									error={errors.cpf?.message}
									value={watch('cpf')}
									displayError
								>
									<Input
										value={maskCPF(watch('cpf') || '')}
										{...register('cpf')}
										data-testid={
											E2E_USER_AUTH.COMPLEMENT_CPF
										}
										onChange={(e) =>
											setValue(
												'cpf',
												maskCPF(e.target.value),
												{
													shouldValidate: true,
												},
											)
										}
										placeholder="000.000.000-00"
										maxLength={14}
										className="h-9 border-stone-200 text-sm"
									/>
								</InputWrapperAuth>
							</div>

							<div className="space-y-1">
								<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
									TELEFONE
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Phone className={iconCommonClass} />}
									error={errors.phone_number?.message}
									value={watch('phone_number')}
									displayError
								>
									<Input
										maxLength={15}
										value={maskPhone(
											watch('phone_number') || '',
										)}
										{...register('phone_number')}
										data-testid={
											E2E_USER_AUTH.COMPLEMENT_PHONE
										}
										onChange={(e) =>
											setValue(
												'phone_number',
												maskPhone(e.target.value),
												{
													shouldValidate: true,
												},
											)
										}
										placeholder="(00) 00000-0000"
										className="h-9 border-stone-200 text-sm"
									/>
								</InputWrapperAuth>
							</div>

							{/* {showCompanyCode && (
                <div className="animate-in fade-in slide-in-from-top-1 mt-2 duration-200">
                  <InputWrapperAuth
                    className="max-w-none"
                    icon={<Building2 className={iconCommonClass} />}
                    error={errors.company_code?.message}
                    value={watch('company_code')}
                    displayError
                  >
                    <Input
                      {...register('company_code')}
                      placeholder="Digite o código da empresa"
                      className="h-9 border-stone-200 text-sm"
                    />
                  </InputWrapperAuth>
                </div>
              )} */}
						</div>

						<div className="space-y-2 py-2">
							<div className="flex items-center gap-2">
								<input
									type="checkbox"
									id="terms_accepted"
									data-testid={E2E_USER_AUTH.COMPLEMENT_TERMS}
									className="h-4 w-4 cursor-pointer accent-stone-900 focus:outline-none"
									{...register('terms_accepted')}
								/>
								<label
									htmlFor="terms_accepted"
									className="flex-1 text-[10px] leading-snug text-stone-500 md:text-xs"
								>
									Confirmo que li e aceito os
									<button
										type="button"
										onClick={() =>
											openModal({
												title: 'Termos de Uso',
												content: <TermsOfUsePage />,
											})
										}
										className="mx-1 font-black text-stone-900 hover:underline"
									>
										termos de uso
									</button>
									e
									<button
										type="button"
										onClick={() =>
											openModal({
												title: 'Política de Privacidade',
												content: <PrivacyPolicyPage />,
											})
										}
										className="mx-1 font-black text-stone-900 hover:underline"
									>
										política de privacidade
									</button>
								</label>
							</div>
							{errors.terms_accepted?.message && (
								<InputError
									error={errors.terms_accepted.message}
								/>
							)}
						</div>

						<div className="flex flex-col gap-3">
							<Button
								type="submit"
								data-testid={E2E_USER_AUTH.COMPLEMENT_SUBMIT}
								disabled={!canSubmitComplementForm}
								className="w-full justify-center rounded-xl border-none bg-[#1a1a1a] py-4 text-sm font-bold text-white transition-all hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#1a1a1a]"
								text="Finalizar Cadastro"
								endIcon={
									<ArrowRightCircle
										size={16}
										className="ml-1"
									/>
								}
							/>
							<button
								type="button"
								onClick={() =>
									navigate('/cadastro', {
										...forwardRegisterFlowNavigateOptions(
											location.state,
										),
									})
								}
								className="flex w-full items-center justify-center gap-2 py-2 text-xs font-bold text-stone-400 transition-colors hover:text-stone-600"
							>
								Voltar para a etapa anterior
							</button>
						</div>
					</form>
				</div>
			</main>
		</div>
	);
};

export { UserRegisterComplementation };
